import React, { useEffect, useState, useCallback } from 'react';
import { faqAPI } from '../api/faq';
import { Search, HelpCircle, ChevronDown, ThumbsUp, ThumbsDown, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function FAQ() {
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [feedbackSent, setFeedbackSent] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, qRes] = await Promise.all([faqAPI.getCategories(), faqAPI.getQuestions({ is_active: true })]);
      setCategories((cRes.data.items || cRes.data || []).filter(c => c.is_active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)));
      setQuestions((qRes.data.items || qRes.data || []).filter(q => q.is_active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch {
      toast.error('Error al cargar ayuda');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    const isOpening = !expanded[id];
    setExpanded(e => ({ ...e, [id]: isOpening }));
    if (isOpening) {
      try { await faqAPI.trackView(id); } catch {}
    }
  };

  const sendFeedback = async (id, useful) => {
    if (feedbackSent[id]) return;
    try {
      await faqAPI.trackFeedback(id, useful);
      setFeedbackSent(f => ({ ...f, [id]: useful ? 'yes' : 'no' }));
      toast.success('Gracias por tu feedback');
    } catch {}
  };

  const filtered = questions.filter(q => {
    if (activeCat && q.category_id !== activeCat) return false;
    if (search) {
      const s = search.toLowerCase();
      return q.question.toLowerCase().includes(s) || q.answer?.toLowerCase().includes(s);
    }
    return true;
  });

  const getCatName = (id) => categories.find(c => c.id === id)?.name || '';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="faq-page">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Centro de Ayuda</h1>
        <p className="text-slate-500 text-sm mt-1">Encuentra respuestas a preguntas frecuentes</p>
      </div>

      <div className="relative">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="pl-10 pr-4 py-3 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
          placeholder="Buscar en preguntas frecuentes..." value={search} onChange={e => setSearch(e.target.value)} aria-label="Buscar ayuda" data-testid="faq-public-search" />
      </div>

      {categories.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setActiveCat(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${!activeCat ? 'bg-soem-primary text-white border-soem-primary' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
            data-testid="faq-cat-all">
            Todas
          </button>
          {categories.map(c => (
            <button key={c.id} onClick={() => setActiveCat(activeCat === c.id ? null : c.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${activeCat === c.id ? 'bg-soem-primary text-white border-soem-primary' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
              data-testid={`faq-cat-${c.id}`}>
              {c.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="soem-card h-14 animate-pulse bg-slate-100" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="soem-card p-12 text-center">
          <HelpCircle size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">{search ? 'Sin resultados para tu busqueda.' : 'No hay preguntas disponibles.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(q => {
            const isOpen = expanded[q.id];
            const fb = feedbackSent[q.id];
            return (
              <div key={q.id} className="soem-card overflow-hidden" data-testid={`faq-q-${q.id}`}>
                <button className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                  onClick={() => toggle(q.id)} data-testid={`faq-toggle-${q.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-soem-navy">{q.question}</p>
                    {!activeCat && <p className="text-xs text-slate-400 mt-0.5">{getCatName(q.category_id)}</p>}
                  </div>
                  <ChevronDown size={16} className={`text-slate-400 flex-shrink-0 ml-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 border-t border-slate-100 pt-3">
                    <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{q.answer}</div>
                    <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
                      <span className="text-xs text-slate-400">Fue util esta respuesta?</span>
                      {fb ? (
                        <span className="flex items-center gap-1 text-xs text-green-600"><Check size={12} /> Gracias</span>
                      ) : (
                        <>
                          <button onClick={() => sendFeedback(q.id, true)}
                            className="flex items-center gap-1 text-xs text-slate-400 hover:text-green-600 transition-colors" data-testid={`faq-useful-${q.id}`}>
                            <ThumbsUp size={12} /> Si
                          </button>
                          <button onClick={() => sendFeedback(q.id, false)}
                            className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors" data-testid={`faq-not-useful-${q.id}`}>
                            <ThumbsDown size={12} /> No
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
